import React, { useState } from 'react';
import { TermsModal, PrivacyPolicyModal, RefundPolicyModal, FAQModal, ContactModal, AboutModal } from './legal/LegalModals';

/* ═══════════════════════════════════════════════════════════
   SiteFooter — 4-column dark navy footer matching brand design
═══════════════════════════════════════════════════════════ */
const SiteFooter = ({ navigate }) => {
  const [modal, setModal] = useState(null);

  const M = (key) => () => setModal(key);

  const SocialIcon = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="w-9 h-9 rounded-xl border border-white/30 hover:border-white/60 hover:bg-white/10 flex items-center justify-center transition-all text-white/90 hover:text-white">
      {children}
    </a>
  );

  return (
    <>
      <footer dir="rtl" className="bg-[#1E3A8A] text-white">
        {/* ── Single row: logo (right) · link nav (middle) · social icons (left) —
             matches the reference's compact one-tier footer. Every link here
             still opens the same modals/routes the old 4-column version did;
             سياسة الإلغاء isn't duplicated here since it's already reachable
             from the first-launch WelcomeBanner and cancellation flows. ── */}
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-6">
          {/* Same real HTML text wordmark as AppNavbar — see the comment there
              for why this isn't an image. */}
          <span className="relative inline-block flex-shrink-0">
            <span className="text-2xl font-black text-white tracking-tight">سافر</span>
            <span className="absolute -top-1 right-1 w-[5px] h-[5px] rounded-full bg-[#FFC928]" aria-hidden="true" />
          </span>

          <nav id="footer-info" className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm order-3 md:order-none w-full md:w-auto">
            {[
              { label: 'عن سافر',           action: M('about')   },
              { label: 'الشروط والأحكام',   action: M('terms')   },
              { label: 'سياسة الخصوصية',    action: M('privacy') },
              { label: 'اتصل بنا',          action: M('contact') },
              { label: 'الأسئلة الشائعة',   action: M('faq')     },
            ].map(({ label, action }) => (
              <button key={label} onClick={action} className="text-white/90 hover:text-white transition-colors font-semibold">
                {label}
              </button>
            ))}
          </nav>

          <div className="flex gap-2.5 flex-shrink-0">
            <SocialIcon href="#">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </SocialIcon>
            <SocialIcon href="#">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </SocialIcon>
            <SocialIcon href="#">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </SocialIcon>
            <SocialIcon href="#">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
              </svg>
            </SocialIcon>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-center">
            <p className="text-xs text-white/70">جميع الحقوق محفوظة © 2026 سافر</p>
            <p className="text-xs text-white/70">من السودان إلى كل الوجهات</p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {modal === 'terms'   && <TermsModal         onClose={() => setModal(null)} />}
      {modal === 'privacy' && <PrivacyPolicyModal  onClose={() => setModal(null)} />}
      {modal === 'refund'  && <RefundPolicyModal   onClose={() => setModal(null)} />}
      {modal === 'faq'     && <FAQModal            onClose={() => setModal(null)} />}
      {modal === 'contact' && <ContactModal        onClose={() => setModal(null)} />}
      {modal === 'about'   && <AboutModal          onClose={() => setModal(null)} />}
    </>
  );
};

export default SiteFooter;
